import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FrameworkRow {
  readonly frameworkId: string;
  readonly frameworkCode: string;
  readonly frameworkName: string;
}

interface ControlRow {
  readonly controlId: string;
  readonly controlCode: string;
  readonly controlName: string;
  readonly controlType: string;
  readonly complianceStatus: string;
  readonly assessmentStatus: string;
}

const COMPLIANCE = ['', 'NOT_ASSESSED', 'COMPLIANT', 'NON_COMPLIANT', 'PARTIALLY_COMPLIANT', 'NOT_APPLICABLE'];

/**
 * GR L30.06 — Compliance Controls (`/security/grc/controls`). Assessor view of
 * the `compliance_control` catalogue, scoped to a chosen `compliance_framework`.
 * Lists each control with its type and compliance/assessment verdict, filterable
 * by compliance status.
 */
export function GrControlsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [frameworks, setFrameworks] = useState<FrameworkRow[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [rows, setRows] = useState<ControlRow[]>([]);
  const [complianceStatus, setComplianceStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: FrameworkRow[] }>('/v1/compliance/frameworks', { token })
      .then((r) => {
        if (!alive) return;
        setFrameworks(r.data);
        setError(null);
        if (r.data.length > 0) setFrameworkId((cur) => cur || r.data[0].frameworkId);
        else setLoading(false);
      })
      .catch((e) => { if (alive) { setError((e as Error).message); setLoading(false); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!frameworkId) return;
    let alive = true;
    setLoading(true);
    const qs = complianceStatus ? `?complianceStatus=${complianceStatus}` : '';
    apiRequest<{ data: ControlRow[] }>(`/v1/compliance/frameworks/${frameworkId}/controls${qs}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, frameworkId, complianceStatus]);

  return (
    <main aria-label="security-controls">
      <h1>{t('gr30.controls.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.controls.labelFramework')}
          <select aria-label="filter-framework" value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
            {frameworks.map((f) => <option key={f.frameworkId} value={f.frameworkId}>{f.frameworkName}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.controls.labelCompliance')}
          <select aria-label="filter-compliance" value={complianceStatus} onChange={(e) => setComplianceStatus(e.target.value)}>
            {COMPLIANCE.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.controls.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.controls.loading')}</p> : frameworks.length === 0 ? (
        <p aria-label="empty">{t('gr30.controls.emptyFrameworks')}</p>
      ) : (
        <>
          <p aria-label="control-count">{t('gr30.controls.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.controls.empty')}</p> : (
            <table aria-label="control-table">
              <thead><tr><th>{t('gr30.controls.colCode')}</th><th>{t('gr30.controls.colName')}</th><th>{t('gr30.controls.colType')}</th><th>{t('gr30.controls.colCompliance')}</th><th>{t('gr30.controls.colAssessment')}</th></tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.controlId}>
                    <td>{c.controlCode}</td><td>{c.controlName}</td><td>{c.controlType}</td>
                    <td>{c.complianceStatus}</td><td>{c.assessmentStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
